// frontend/src/components/EmpresaPortal/TourOnboarding.js
//
// Product tour de bienvenida para el portal de empresa: se muestra
// automáticamente solo la primera vez que un usuario inicia sesión
// (usuario.tour_visto === false, ver backend/src/routes/auth.js), recorre
// el flujo completo de invitar a un candidato, y se puede volver a ver
// manualmente con el botón "🎓 Ver tour".

import React, { useState, useEffect, useCallback } from 'react';
import { Joyride, STATUS, EVENTS, ACTIONS } from 'react-joyride';
import { X, GraduationCap } from 'lucide-react';
import './TourOnboarding.css';

const PASOS = [
  {
    target: 'body',
    placement: 'center',
    title: '¡Bienvenido a PsyTest! 👋',
    content: 'Te mostramos en unos pasos cómo invitar a tu primer candidato a una evaluación. Solo toma un minuto.'
  },
  {
    target: '[data-tour="cupo"]',
    title: 'Tus créditos',
    content: 'Aquí ves tus créditos libres. Una prueba individual cuesta 1 crédito y un stack de hasta 3 pruebas cuesta 2. Se cobran cuando el candidato inicia su evaluación. Al registrarte te regalamos créditos para empezar.'
  },
  {
    target: '[data-tour="stack-select"]',
    title: 'Elige qué vas a evaluar',
    content: 'Selecciona uno de nuestros stacks de pruebas ya armados (razonamiento, personalidad, etc.), o uno que hayas creado tú mismo.'
  },
  {
    target: '[data-tour="form-candidato"]',
    title: 'Agrega a tu candidato',
    content: 'Captura su nombre completo, correo y CURP. Con eso es suficiente para generar su enlace de evaluación.'
  },
  {
    target: '[data-tour="btn-invitar"]',
    title: '¡Listo para enviar!',
    content: 'Al hacer clic aquí, tu candidato recibe un correo con el enlace para presentar la prueba desde cualquier dispositivo.'
  },
  {
    target: '[data-tour="crear-prueba"]',
    title: '¿Quieres crear tus propias preguntas?',
    content: 'Desde aquí puedes diseñar tus propios cuestionarios y combinarlos con las pruebas oficiales, cuando quieras.'
  },
  {
    target: '[data-tour="configuracion"]',
    title: 'Tu configuración',
    content: 'Aquí encuentras los datos de tu perfil. Pronto agregaremos más opciones de configuración en esta misma sección.'
  }
];

const TourTooltip = ({ continuous, index, step, backProps, closeProps, primaryProps, skipProps, size }) => {
  const esUltimo = index === size - 1;
  return (
    <div className="tour-tooltip">
      <button {...closeProps} className="tour-tooltip-cerrar" aria-label="Cerrar tour"><X size={14} /></button>
      {step.title && <h4 className="tour-tooltip-title">{step.title}</h4>}
      <div className="tour-tooltip-content">{step.content}</div>
      <div className="tour-tooltip-footer">
        <span className="tour-tooltip-progress">Paso {index + 1} de {size}</span>
        <div className="tour-tooltip-actions">
          {index > 0 && <button {...backProps} className="tour-btn-secondary">Atrás</button>}
          {!esUltimo && <button {...skipProps} className="tour-btn-link">Saltar tour</button>}
          {continuous && (
            <button {...primaryProps} className="tour-btn-primary">
              {esUltimo ? 'Finalizar' : 'Siguiente'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

const TourOnboarding = ({ usuario, onTourVisto }) => {
  const [run, setRun] = useState(false);
  const [stepIndex, setStepIndex] = useState(0);

  useEffect(() => {
    if (usuario && usuario.tour_visto === false) {
      // Pequeño delay para que el resto del portal ya haya terminado de
      // montar (candidatos, stacks, etc.) antes de buscar los elementos.
      const t = setTimeout(() => setRun(true), 600);
      return () => clearTimeout(t);
    }
  }, [usuario]);

  const marcarVisto = useCallback(async () => {
    try {
      await fetch('/api/auth/marcar-tour-visto', { method: 'POST', credentials: 'include' });
    } catch (e) { /* si falla, en el peor caso se repite la próxima vez */ }
    if (onTourVisto) onTourVisto();
  }, [onTourVisto]);

  const handleCallback = (data) => {
    const { status, index, type, action } = data;

    if (status === STATUS.FINISHED || status === STATUS.SKIPPED) {
      setRun(false);
      setStepIndex(0);
      marcarVisto();
      return;
    }

    if (type === EVENTS.STEP_AFTER || type === EVENTS.TARGET_NOT_FOUND) {
      setStepIndex(index + (action === ACTIONS.PREV ? -1 : 1));
    }
  };

  const iniciarManualmente = () => {
    setStepIndex(0);
    setRun(true);
  };

  return (
    <>
      <Joyride
        steps={PASOS}
        run={run}
        stepIndex={stepIndex}
        continuous
        onEvent={handleCallback}
        tooltipComponent={TourTooltip}
        locale={{ back: 'Atrás', close: 'Cerrar', last: 'Finalizar', next: 'Siguiente', skip: 'Saltar tour' }}
        options={{ overlayClickAction: false }}
        styles={{
          options: {
            primaryColor: '#287E73',
            overlayColor: 'rgba(15, 23, 42, 0.55)',
            zIndex: 10000
          }
        }}
      />
      <button type="button" className="tour-btn-replay" onClick={iniciarManualmente} title="Ver el recorrido guiado de nuevo">
        <GraduationCap size={16} /> Ver tour
      </button>
    </>
  );
};

export default TourOnboarding;
